import i18next from 'i18next';
import { CSSProperties, ReactElement, ReactNode, useState } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { ChevronRight } from 'react-bootstrap-icons';
import DataTablePagination, { DataTablePaginationState } from './DataTablePagination';

const DEFAULT_PAGE_SIZE: number = 10;

export type DataTableColumn<T> = {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  width?: CSSProperties['width'];
  align?: 'start' | 'center' | 'end';
};

type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  emptyText?: string;
  toolbar?: ReactNode;
  pagination?: DataTablePaginationState;
  pageSize?: number;
};

const DataTable = <T,>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isLoading = false,
  emptyText = i18next.t('generic.noEntries'),
  toolbar,
  pagination,
  pageSize = DEFAULT_PAGE_SIZE,
}: DataTableProps<T>): ReactElement => {
  const [clientPage, setClientPage] = useState(0);

  const columnCount: number = columns.length + (onRowClick ? 1 : 0);
  const clientPageCount: number = Math.max(Math.ceil(rows.length / pageSize), 1);

  const activePagination: DataTablePaginationState = pagination ?? {
    page: Math.min(clientPage, clientPageCount - 1),
    pageCount: clientPageCount,
    pageSize,
    totalCount: rows.length,
    isFetching: false,
    setPage: setClientPage,
  };

  const visibleRows: T[] = pagination
    ? rows
    : rows.slice(activePagination.page * pageSize, (activePagination.page + 1) * pageSize);

  const renderMessageRow = (content: ReactNode): ReactElement => {
    return (
      <tr>
        <td colSpan={columnCount} className="text-center text-muted py-5">
          {content}
        </td>
      </tr>
    );
  };

  const renderBody = (): ReactNode => {
    if (isLoading) {
      return renderMessageRow(<Spinner animation="border" size="sm" />);
    }

    if (visibleRows.length === 0) {
      return renderMessageRow(emptyText);
    }

    return visibleRows.map((row: T) => (
      <tr
        key={rowKey(row)}
        onClick={onRowClick ? (): void => onRowClick(row) : undefined}
        style={{ height: 64, cursor: onRowClick ? 'pointer' : 'default' }}>
        {columns.map((column: DataTableColumn<T>) => (
          <td key={column.key} className={`px-3 py-2 text-${column.align ?? 'start'}`}>
            {column.cell(row)}
          </td>
        ))}

        {onRowClick && (
          <td className="px-3 py-2 text-end text-muted">
            <ChevronRight />
          </td>
        )}
      </tr>
    ));
  };

  return (
    <div className="card border-0 shadow-sm overflow-hidden">
      {toolbar && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-3 border-bottom">
          {toolbar}
        </div>
      )}

      <div className="table-responsive">
        <Table hover className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              {columns.map((column: DataTableColumn<T>) => (
                <th
                  key={column.key}
                  className={`px-3 py-3 text-${column.align ?? 'start'} text-uppercase text-muted fw-semibold small text-nowrap`}
                  style={{ width: column.width, letterSpacing: '0.04em' }}>
                  {column.header}
                </th>
              ))}

              {onRowClick && <th className="px-3 py-3" style={{ width: 48 }} />}
            </tr>
          </thead>

          <tbody>{renderBody()}</tbody>
        </Table>
      </div>

      {!isLoading && <DataTablePagination pagination={activePagination} />}
    </div>
  );
};

export default DataTable;
